import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import styles from '../../../style';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { loadOrders, downloadPDF } from 'services/orders/action';
import { connect } from 'react-redux';
import moment from 'moment';
import { downloadPDFRx } from 'services/soap/action';

const TableRowsLoader = ({ rowsNum = 8 }) => (
  <>
    {[...Array(rowsNum)].map((_, i) => (
      <TableRow key={i}>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
        <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
      </TableRow>
    ))}
  </>
);

const OrderHx = props => {
  const { classes, patientInfo, ehrID, loadOrders } = props;

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [result, setResult] = useState([]);
  const [fetching, setFetching] = useState(true);  // initial table load
  const [loading, setLoading] = useState(false);    // PDF download overlay

  const fetchData = useCallback(async (patientId, rows, pg) => {
    try {
      setFetching(true);
      const allorders = await loadOrders(patientId, rows, pg);
      setResult(allorders?.data?.content ?? []);
      setTotalElements(allorders?.data?.totalElements ?? 0);
    } catch (error) {
      setResult([]);
    } finally {
      setFetching(false);
    }
  }, [loadOrders]);

  useEffect(() => {
    if (patientInfo?.id) {
      fetchData(patientInfo.id, rowsPerPage, page);
    }
  }, [patientInfo, fetchData]);

  const openPDF = data => {
    const file = new Blob([data], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, `_blank_${Date.now()}`);
  };

  const handleView = async (type, providerId, orderHx) => {
    let orderIds; 
    if (type === 'Prescription') orderIds = orderHx.medIds;
    else if (type === 'Lab Order') orderIds = orderHx.labOrderIds;
    else if (type === 'Imaging Order') orderIds = orderHx.imgOrderIds;
    else if (type === 'Physician Order') orderIds = orderHx.phyOrderIds;

    if (!orderIds) {
      window.alert('No order history available.');
      return;
    }

    try {
      setLoading(true);
      const res = await props.downloadPDF(ehrID, patientInfo.id, providerId, type, orderIds);
      if (res?.status) {
        openPDF(res.result.data);
      }
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(patientInfo.id, rowsPerPage, newPage);
  };

  const handleChangeRowsPerPage = event => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData(patientInfo.id, newRowsPerPage, 0);
  };

  const hasResults = Array.isArray(result) && result.length > 0;

  return (
    <>
      {/* PDF download overlay */}
      {loading && (
        <Box
          style={{
            position: 'fixed',
            top: 0, left: 0,
            width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.3)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Flex column so pagination + footer stay pinned at bottom */}
      <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Scrollable table area */}
        <PerfectScrollbar style={{ flex: 1 }}>
          <div style={{ height: 340 }}>
            <Table
              className={classes.generalTable}
              style={{ tableLayout: 'fixed', width: '100%' }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 100, whiteSpace: 'nowrap' }}>Date</TableCell>
                  <TableCell style={{ width: 130, whiteSpace: 'nowrap' }}>Type</TableCell>
                  <TableCell style={{ width: 150, whiteSpace: 'nowrap' }}>Physician</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetching ? (
                  <TableRowsLoader rowsNum={8} />
                ) : hasResults ? (
                  result.map(itemOrder => (
                    <TableRow
                      key={itemOrder.id ?? `${itemOrder.createdDate}-${itemOrder.type}`}
                      hover
                      onClick={() => handleView(itemOrder.type, itemOrder.providerId, itemOrder)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {itemOrder.createdDate
                          ? moment(itemOrder.createdDate).format('MM/DD/YYYY')
                          : '—'}
                      </TableCell>
                      <TableCell>{itemOrder.type}</TableCell>
                      <TableCell>{itemOrder.providerName}</TableCell>
                      <TableCell
                        style={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          maxWidth: 0
                        }}
                      >
                        {itemOrder.recap}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      style={{
                        textAlign: 'center',
                        color: 'rgba(0,0,0,0.4)',
                        fontStyle: 'italic'
                      }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>

        {/* Pagination — only when there are results */}
        {hasResults && (
          <TablePagination
            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
            className={classes.paginationWrap}
            component="div"
            count={totalElements}
            nextIconButtonProps={{ 'aria-label': 'Next Page' }}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 15]}
          />
        )}

        {/* Footer */}
        <Box className={classes.modalFooter}>
          <Button
            color="primary"
            onClick={props.onCancel}
            size="small"
            style={{ borderRadius: 4 }}
            variant="contained"
          >
            Close
          </Button>
        </Box>

      </Box>
    </>
  );
};

OrderHx.propTypes = {
  className: PropTypes.string
};

const mapDispatchToProps = { loadOrders, downloadPDF, downloadPDFRx };

const mapStateToProps = state => ({
  ehrID: state.profile?.ehrID,
  soap: state.soap
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(OrderHx));